/**
 * @ais/dashboard-widgets · DashboardWidget contract.
 *
 * Mirrors `ViewerPlugin` from `@ais/viewer-core` but for non-3D cell types in
 * the supervision dashboard. The dashboard owns the grid; each cell mounts one
 * widget instance via `widget.mount(host, config, ctx)` and disposes it on
 * cell teardown / re-mount.
 *
 * Shared resources (large blobs like base64 images or embedded snapshots) live
 * in `layout.resources` at the bottom of the layout JSON, glTF-style. Cells
 * reference them via `{ $ref: 'res-xyz' }` placed inside their config / state.
 * The dashboard wires up `addResource` / `getResource` on the ctx and GCs
 * unreferenced resources at save time.
 */

import type { AssetPickerKind } from '@ais/viewer-ui';

export type ResourceType = 'image' | 'snapshot';

export interface ResourceEntry {
  type: ResourceType;
  /** Original filename for display. */
  name?: string;
  /** For images / PDFs. */
  mimeType?: string;
  /** Base64 (no `data:` prefix) — used by image resources. */
  data?: string;
  /** JSON content — used by snapshot resources. */
  content?: unknown;
}

/** A placeholder a cell stores in its config / state to reference a resource. */
export interface ResourceRef {
  $ref: string;
}

export function isResourceRef(v: unknown): v is ResourceRef {
  return !!v && typeof v === 'object' && typeof (v as ResourceRef).$ref === 'string';
}

/**
 * ---------------------------------------------------------------------------
 * The cell bus — how one cell reads another cell's output
 * ---------------------------------------------------------------------------
 *
 * A cell hosting the 3D viewer can be running SEVERAL plugins at once (its
 * snapshot names them all), so a reader cell cannot address "the slope cell" —
 * it addresses `cell → plugin`. That is exactly how the engine already stores
 * this: `viewer.execLog` is keyed by plugin id and every `ResultEntry` carries
 * its `producer`. The feed below is that shape, flattened to plain JSON so a
 * widget bundle can read it without importing the engine.
 *
 * The bus is STICKY: a reader mounted after the producer already ran gets the
 * current value immediately rather than waiting for the next run.
 */

/** One recorded `viewer.exec` run — mirrors viewer-core's `PluginExecRecord`. */
export interface CellRun {
  /** Monotonic per-plugin id (`exec-1`, `exec-2`, …). */
  id: string;
  /** ISO time the run was recorded. */
  time: string;
  input?: unknown;
  result?: unknown;
  /** Set instead of `result` when the run threw. */
  error?: string;
}

/** One plugin live inside a publishing cell, newest run last. */
export interface CellPlugin {
  id: string;
  label?: string;
  runs: CellRun[];
}

/** A derived-data entry from the publishing cell's `viewer.results`. */
export interface CellResult {
  id: string;
  kind: string;
  label: string;
  producer?: string;
  semantic?: string;
}

/** What a cell announces about its own output. */
export interface CellFeed {
  cellId: string;
  widgetType: string;
  /** Human label for a reader's source picker (e.g. the loaded snapshot name). */
  title?: string;
  plugins: CellPlugin[];
  results: CellResult[];
}

/** The half of a {@link CellFeed} a publisher supplies; the host stamps the rest. */
export type CellFeedInput = Omit<CellFeed, 'cellId' | 'widgetType'>;

/** A cell on the current tab, for a reader widget's source picker. */
export interface CellSummary {
  id: string;
  type: string;
  /** The feed's title, when the cell is publishing one. */
  title?: string;
  /** False for a cell that publishes nothing — listed so the picker can say so. */
  publishes: boolean;
}

export interface DashboardCtx {
  /** Unique id of the cell this widget instance is mounted into. */
  cellId: string;
  /** Type-id of the widget (matches DashboardWidget.id). */
  widgetType: string;
  /** Register a shared resource; returns a ref to embed in widget config / state. */
  addResource(entry: ResourceEntry): ResourceRef;
  /** Resolve a previously-added resource. Accepts either the ref object or its `$ref` id. */
  getResource(ref: ResourceRef | string | null | undefined): ResourceEntry | undefined;
  /** Drop a resource from the layout. Usually not needed — the host GCs unreferenced ones at save time. */
  removeResource(ref: ResourceRef | string): void;
  /**
   * Optional host capability: open a picker over the site's saved results and
   * resolve with the chosen result's URL (or `null` on cancel). Set only by
   * hosts wired to a site NAS (base-portable builds); a widget that offers a
   * "pick from site" affordance should render it ONLY when this is present, so
   * the default build keeps today's local-file-only behavior. The URL is
   * directly fetchable — the widget loads it via its normal URL path.
   */
  pickResultUrl?(): Promise<string | null>;
  /**
   * Optional host capability: open a picker over the site's stored MODELS for
   * one asset kind and resolve with a directly loadable URL (or `null` on
   * cancel). Same gate and same contract as {@link pickResultUrl} — set only by
   * hosts wired to a site NAS, so the default build keeps local-file-only.
   *
   * A cell hosting the 3D viewer hands this straight to the Sidebar's
   * `assetPicker`, which is what makes its scene sections offer the same browse
   * buttons the editor has. Without it a cell could only load models the
   * operator had a local copy of — the scene it was showing came from the site,
   * so being unable to add anything else from the site read as the cell running
   * a different, lesser build of the viewer.
   */
  pickAssetUrl?(kind: AssetPickerKind): Promise<string | null>;

  /**
   * Optional host capability: store a file the operator picked and return a
   * durable URL for it.
   *
   * For anything SMALL, `addResource` is the right home — the bytes ride inside
   * the layout JSON and the board stays self-contained. Video is the case where
   * that breaks down: base64 inflates by a third, so a 50 MB clip would put
   * ~67 MB into every save, publish and reload of the board. A widget holding
   * media that large stores a URL and lets the file live in the host's store.
   *
   * The host owns WHERE (`/files` standalone, the site's own prefix on a hub) —
   * a widget must never build that path itself. Absent capability means the
   * build has nowhere durable to put bytes; the widget should fall back to an
   * object URL and say plainly that it lasts the session.
   */
  uploadFile?(file: File): Promise<string | null>;

  /**
   * Optional host capability: announce this cell's current output to the board.
   * Sticky — a reader that mounts later receives the last value immediately.
   * Pass `null` to withdraw (the cell cleared, or is being disposed).
   *
   * Optional because a host predating the bus does not provide it; a publisher
   * must tolerate its absence and simply stay silent.
   */
  publish?(feed: CellFeedInput | null): void;
  /**
   * Optional host capability: watch every publishing cell on the current tab.
   * Fires immediately with the current map, then on every change. Returns an
   * unsubscribe the widget MUST call from `dispose`.
   */
  subscribe?(fn: (feeds: ReadonlyMap<string, CellFeed>) => void): () => void;
  /** Optional host capability: the cells sharing this tab, excluding self. */
  cells?(): CellSummary[];
}

export interface DashboardWidgetInstance {
  /** Tear down everything mounted into `host`. Must release listeners + GPU resources. */
  dispose(): void;
  /** Optional state round-trip — runs at layout save. Must be JSON-serialisable. */
  getState?(): unknown;
  /** Optional state restore — runs after `mount` on layout load. */
  applyState?(state: unknown): void | Promise<void>;
  /** Optional config form for edit mode. `onChange` receives the new config (which the host then re-mounts). */
  renderConfigUI?(host: HTMLElement, onChange: (cfg: unknown) => void): void;
  /**
   * Optional: the cell was maximised to fill the board, or restored.
   *
   * A widget that only draws content can ignore this. The viewer cell uses it
   * to show its bars: viewer-ui's sidebar / toolbar / info-history are
   * viewport-fixed singletons, so real chrome only makes sense — and only
   * lands correctly — while one cell IS the viewport.
   */
  setMaximised?(on: boolean): void;
  /**
   * Optional: the cell's tab was left (false) or returned to (true). The board
   * keeps recently visited tabs alive instead of rebuilding them, so a hidden
   * widget should stop its expensive work — a viewer its render loop, a video
   * its decode — and pick it back up on true.
   */
  setVisible?(visible: boolean): void;
}

export interface DashboardWidget {
  readonly id: string;
  readonly label?: string;
  /** Optional one-line description for the type-picker. */
  readonly description?: string;
  /** Optional hint for the edit-mode form (free-form JSON-schema-ish; renderer-defined). */
  readonly configSchema?: unknown;
  /** Create a live instance mounted into `host`. Called per-cell. */
  mount(host: HTMLElement, config: unknown, ctx: DashboardCtx): DashboardWidgetInstance;
}
